import React from "react";
import classnames from "classnames";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Label,
  FormGroup,
  InputGroup,
  Form,
  Input,
  FormFeedback,
} from "reactstrap";
import { ToastContainer } from "react-toastify";
import { CONTACT_EMAIL_VARIABLE } from "@/utils/siteSettings";
import type { SitePermission } from "@/services/userServices/useCurrentPermission";

/** One entry of the site's `settings_variables`, as the form below reads it. */
type SettingEntry = { variable?: string; type?: string; value?: unknown };

/**
 * The site document stores a setting's value untyped (the container keeps it
 * `unknown`); the inputs show it exactly as the JavaScript passed it on.
 */
const inputValue = (value: unknown) => value as string | number | undefined;

export type UpdateSiteConfigurationsViewProps = {
  current?: string;
  permission?: Readonly<SitePermission> | null;
  settingsVariables: SettingEntry[];
  onChangeSettings: (e: React.ChangeEvent<HTMLInputElement>, index: number) => void;
  onClickAddSettings: () => void;
  onClickUpdateSettings: () => void;
  onClickDeleteAsk: () => void;
  alertNode?: React.ReactNode;
  contactEmailInvalid?: boolean;
};

const UpdateSiteConfigurationsView = ({
  current,
  permission,
  settingsVariables,
  onChangeSettings,
  onClickAddSettings,
  onClickUpdateSettings,
  onClickDeleteAsk,
  alertNode,
  contactEmailInvalid = false,
}: UpdateSiteConfigurationsViewProps) => {
  const contactEmailIndex = settingsVariables.findIndex(
    (entry) => entry.variable === CONTACT_EMAIL_VARIABLE
  );
  const contactEmailEntry =
    contactEmailIndex >= 0 ? settingsVariables[contactEmailIndex] : null;
  const otherVariables = settingsVariables.filter(
    (entry) => entry.variable !== CONTACT_EMAIL_VARIABLE
  );

  return (
    <>
      {/* ALERT */}
      {alertNode}

      <Card>
        {/* NOTIFICATION DIV */}
        <div className="rna-wrapper">
          <ToastContainer />
        </div>

        <CardHeader>
          <Row className="text-center">
            <Col xs="8">
              <h5 className="h3 mb-0">
                Update Site Configurations for {current}
              </h5>
            </Col>
            <Col className="text-right" xs="4">
              {permission?.publish && permission?.edit ? (
                <Button color="danger" size="sm" onClick={onClickDeleteAsk}>
                  Delete Configurations
                </Button>
              ) : null}
            </Col>
          </Row>
        </CardHeader>

        <CardBody className="ml-5">
          {contactEmailEntry && contactEmailIndex >= 0 && (
            <Form>
              <FormGroup className="row">
                <small style={{ color: "red" }}>*</small>
                <Label className="form-control-label" md="2">
                  Contact Email
                </Label>
                <Col md="7">
                  <InputGroup className={classnames("input-group-merge")}>
                    <Input
                      type="email"
                      name={CONTACT_EMAIL_VARIABLE}
                      placeholder="contact@example.com"
                      onChange={(e) => onChangeSettings(e, contactEmailIndex)}
                      value={inputValue(contactEmailEntry.value) || ""}
                      required
                      invalid={contactEmailInvalid}
                    />
                    <FormFeedback>
                      Contact Email is required and must be a valid email address.
                    </FormFeedback>
                  </InputGroup>
                  <small className="text-muted">
                    SES sender and recipient for contact form submissions on this site.
                  </small>
                </Col>
              </FormGroup>
            </Form>
          )}

          {otherVariables.map((prop, index) => {
            const settingsIndex = settingsVariables.findIndex(
              (entry) => entry.variable === prop.variable
            );
            return (
            <React.Fragment key={`${prop.variable}-${index}`}>
              <Form>
                {/* STRING */}
                {(prop.type?.toLowerCase?.() === "string" || prop.type === "") && (
                  <FormGroup className="row">
                    <small style={{ color: "red" }}>*</small>
                    <Label className="form-control-label" md="2">
                      {prop.variable}
                    </Label>
                    <Col md="7">
                      <InputGroup className={classnames("input-group-merge")}>
                        <Input
                          type="text"
                          name={prop.variable}
                          onChange={(e) => onChangeSettings(e, settingsIndex)}
                          value={inputValue(prop.value)}
                        />
                      </InputGroup>
                    </Col>
                  </FormGroup>
                )}

                {/* NUMBER */}
                {prop.type?.toLowerCase?.() === "number" && (
                  <FormGroup className="row">
                    <small style={{ color: "red" }}>*</small>
                    <Label className="form-control-label" md="2">
                      {prop.variable}
                    </Label>
                    <Col md="7">
                      <Input
                        type="number"
                        name={prop.variable}
                        onChange={(e) => onChangeSettings(e, settingsIndex)}
                        value={inputValue(prop.value)}
                      />
                    </Col>
                  </FormGroup>
                )}

                {/* BOOLEAN */}
                {prop.type?.toLowerCase?.() === "boolean" && (
                  <FormGroup className="row">
                    <small style={{ color: "red" }}>*</small>
                    <Label className="form-control-label" md="2">
                      {prop.variable}
                    </Label>
                    <Col md="7" className="mt-2">
                      <InputGroup className={classnames("input-group-merge")}>
                        <div className="custom-control custom-radio mb-3">
                          <input
                            className="custom-control-input"
                            id={`${prop.variable}-1`}
                            name={prop.variable}
                            type="radio"
                            value={"true"}
                            onChange={(e) => onChangeSettings(e, settingsIndex)}
                            defaultChecked={inputValue(prop.value)?.toString() === "true"}
                          />
                          <label
                            className="custom-control-label"
                            htmlFor={`${prop.variable}-1`}
                          >
                            Yes
                          </label>
                        </div>
                        <div className="custom-control custom-radio ml-4">
                          <input
                            className="custom-control-input"
                            id={`${prop.variable}-2`}
                            name={prop.variable}
                            type="radio"
                            value={"false"}
                            onChange={(e) => onChangeSettings(e, settingsIndex)}
                            defaultChecked={inputValue(prop.value)?.toString() === "false"}
                          />
                          <label
                            className="custom-control-label"
                            htmlFor={`${prop.variable}-2`}
                          >
                            No
                          </label>
                        </div>
                      </InputGroup>
                    </Col>
                  </FormGroup>
                )}
              </Form>
            </React.Fragment>
            );
          })}

          {permission?.publish && permission?.edit ? (
            <FormGroup className="row">
              <Col className="text-right" lg="9">
                <Button className="my-4" color="primary" onClick={onClickAddSettings}>
                  Add Settings
                </Button>
                <Button className="my-4" color="success" onClick={onClickUpdateSettings}>
                  Update Settings
                </Button>
              </Col>
            </FormGroup>
          ) : null}
        </CardBody>
      </Card>
    </>
  );
};

export default React.memo(UpdateSiteConfigurationsView);
